import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_OTAR_CONNECTION_FORM_FIELDS,
  EDIT_OTAR_CONNECTION_FORM_FIELDS,
  OTAR_FILTER_COLUMNS,
  OTAR_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformOTARResponseRow,
  transformOTARRows,
  transformOTARUpdateBody,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteOTARConnection,
  getOTARConnectionByRef,
  updateOTARConnectionByRef,
} from "../api";
import EditRow from "../components/dataTable/EditRow";
import { INITIAL_OTAR_EDIT_STATE } from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_OTAR_CONNECTION_TRANSLATIONS,
  READ_OTAR_CONNECTION_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
  "KMF-RADIO-SET-IDENTIFIER"?: string | null;
}

const INITIAL_STATE = {
  "CONNECTION-ID": "",
  "KMF-RADIO-SET-IDENTIFIER": "",
};

const searchFields = ["CONNECTION-ID", "KMF-RADIO-SET-IDENTIFIER"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_OTAR_CONNECTION_FORM_FIELDS
);

const OTARConnections = () => {
  const {
    token,
    scopes: { otar_connections: otarConnections },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle("OTAR Connections- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [otarData, setOtarData] = useState<any[]>([]);

  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });
  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const fetchOTARConnections = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
          ...(data &&
            data["KMF-RADIO-SET-IDENTIFIER"] && {
              "KMF-RADIO-SET-IDENTIFIER": data["KMF-RADIO-SET-IDENTIFIER"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/otar-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setOtarData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchOTARConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteOTARConnection({
          token,
          org: selectedOrg.id,
          ref,
        });
        successAlert(DELETE_OTAR_CONNECTION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchOTARConnections();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_OTAR_CONNECTION_TRANSLATIONS.header}
        pageDescription={READ_OTAR_CONNECTION_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="OTAR Connection"
              pathName="/api/v1/app-config/otar-connections"
              csvTemplateFile={[
                "CONNECTION-ID,KMF-IPV4-ADDR,KMF-PORT,KMF-RADIO-SET-IDENTIFIER,IWF-RADIO-SET-IDENTIFIER,MESSAGE-NUMBER-PERIOD,PRIVATE-CALL-SLN,GROUP-REGROUP-SLN,OTAR-REGISTRATION-TIMER,KEEP-ALIVE-TIMER,OPT-SEND-REKEY-REQ,OPT-DE-REGISTRATION,OPT-REVERSE-WARM-START,OPT-HELLO-MAC-REPLY,OPT-KEY-ASSIGNMENT\n",
                ",,,,,,,8,0,,,,,,",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchOTARConnections();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditRow
              panelName={READ_OTAR_CONNECTION_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_OTAR_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              formFields={EDIT_OTAR_CONNECTION_FORM_FIELDS}
              header={READ_OTAR_CONNECTION_TRANSLATIONS.editRow.header}
              transformResponseRow={transformOTARResponseRow}
              transformUpdateBody={transformOTARUpdateBody}
              onUpdate={updateOTARConnectionByRef}
              loadDataByRef={getOTARConnectionByRef}
              hasDeletePermission={
                otarConnections && otarConnections.includes("d")
              }
              hasEditPermission={
                otarConnections && otarConnections.includes("u")
              }
              refreshTable={fetchOTARConnections}
              deleteModalItemName="CONNECTION-ID"
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={OTAR_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={otarConnections && otarConnections.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/otarConnections/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformOTARRows(otarData).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={OTAR_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={
              otarConnections && otarConnections.includes("c")
            }
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_OTAR_CONNECTION_TRANSLATIONS.infoAlert.heading}
          subText={READ_OTAR_CONNECTION_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default OTARConnections;
